import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import { postAiJob } from "@/utils/aiJobs";
import {
  BarChart3, TrendingUp, Gauge, Sparkles, Target, ShieldCheck,
  ArrowRight, Loader2, Check, X, AlertTriangle, Brain, FileText,
  Clock, MapPin, Euro, Percent, Copy, CheckCircle2, Lightbulb,
  Archive, Swords, Plus, Search, Tag, Trash2, Edit, Wand2, BookOpen, Trophy,
  Building2, BookMarked, Users, Signal, ClipboardCheck, PhoneCall
} from "lucide-react";
import { toast } from "sonner";
import { ESTADO_LABELS, ESTADO_CLS, fmtEur, Field, Kpi } from "./shared";

export default function BidAIKnowledge() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [selected, setSelected] = useState(null);
  const [showAI, setShowAI] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (filterSection) params.append("seccion", filterSection);
    try {
      const r = await axios.get(`${API}/tender360/best-practices?${params.toString()}`);
      setData(r.data);
    } catch { toast.error("Error cargando best practices"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, filterSection]);
  // P0-04 (plan cierre POC/Comité 12/10/2026) · "Mi Trabajo" (evidencia sin
  // verificar) sólo abría la pestaña, nunca el registro concreto — aquí es
  // maestro/detalle, así que auto-selecciona el item al llegar con "?open=".
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("open");
    if (id && data?.items) {
      const bp = data.items.find((x) => x.id === id);
      if (bp) setSelected(bp);
    }
  }, [data]);

  const reuse = async (id) => {
    try {
      await axios.post(`${API}/tender360/best-practices/${id}/reuse`);
      navigator.clipboard.writeText(selected?.contenido || "");
      toast.success("Copiado + reutilización registrada");
      load();
    } catch { toast.error("Error"); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este best practice?")) return;
    try { await axios.delete(`${API}/tender360/best-practices/${id}`); toast.success("Eliminado"); load(); setSelected(null); }
    catch { toast.error("Error"); }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="bidai-knowledge">
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Respuestas en biblioteca"  val={data.kpis.total}/>
        <Kpi label="Completitud media (heurística, no evalúa calidad técnica)" val={`${data.kpis.avg_ia_score}/100`}/>
        <Kpi label="Secciones cubiertas"       val={data.kpis.sections}/>
      </div>

      {data.kpis.sin_evidencia_documental > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex items-center gap-2" data-testid="bp-sin-evidencia-banner">
          <AlertTriangle size={14} />
          <span><b>{data.kpis.sin_evidencia_documental}</b> de {data.kpis.total} respuestas reutilizables no tienen ninguna evidencia documental verificada. Sus cifras de score/mejora técnica son afirmaciones pendientes de soporte, no promesas acreditadas.</span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center border border-gray-200 rounded-lg px-2 py-1 gap-1">
          <Search size={12} className="text-gray-400"/>
          <input placeholder="Buscar por texto/tag..." value={q} onChange={e=>setQ(e.target.value)}
                 className="text-xs outline-none w-48" data-testid="bp-search-input"/>
        </div>
        <select value={filterSection} onChange={e=>setFilterSection(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white">
          <option value="">Todas las secciones</option>
          {(data.secciones || []).map(s => <option key={s}>{s}</option>)}
        </select>
        <div className="flex-1"/>
        <button onClick={()=>setShowAI(true)}
                className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-[#006528]"
                data-testid="bp-ai-generate-btn">
          <Wand2 size={12}/> Generar con Pando
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
            {(data.items || []).map(bp => (
              <button key={bp.id} onClick={()=>setSelected(bp)}
                      className={`w-full text-left p-3 hover:bg-gray-50 ${selected?.id===bp.id ? "bg-gray-50" : ""}`}
                      data-testid={`bp-item-${bp.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-gray-900 truncate">{bp.titulo}</div>
                  {bp.ai_generated && <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-semibold">Pando</span>}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{bp.seccion}</span>
                  {bp.evidence_status === "con_evidencia_verificada"
                    ? <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold">Evidencia verificada</span>
                    : <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">Sin evidencia</span>}
                  <span className="text-[10px] text-gray-500">♻ {bp.times_reused || 0}</span>
                </div>
              </button>
            ))}
            {!data.items?.length && <div className="p-6 text-center text-gray-400 text-xs">Sin respuestas</div>}
          </div>
        </div>

        <div className="lg:col-span-3">
          {!selected
            ? <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-xs">Selecciona una respuesta para ver el detalle</div>
            : (
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="bp-detail">
                <div className="flex items-start justify-between gap-2 border-b border-gray-100 pb-3">
                  <div>
                    <div className="text-sm font-bold text-gray-900">{selected.titulo}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{selected.seccion}</span>
                      {selected.evidence_status === "con_evidencia_verificada" ? (
                        <>
                          {selected.ia_score != null && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-semibold">Score {selected.ia_score} (evidencia verificada)</span>}
                          {selected.uplift_estimado_pct != null && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">+{selected.uplift_estimado_pct}pp técnica (evidencia verificada)</span>}
                        </>
                      ) : (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-semibold">Sin evidencia documental — cifra pendiente de soporte</span>
                      )}
                      <span className="text-[10px] text-gray-500">♻ {selected.times_reused || 0} usos</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={()=>reuse(selected.id)} className="text-xs bg-[#007934] text-white px-2 py-1.5 rounded flex items-center gap-1 hover:bg-[#006528]" data-testid="bp-reuse-btn">
                      <Copy size={11}/> Copiar + reutilizar
                    </button>
                    <button onClick={()=>del(selected.id)} className="text-xs text-rose-600 hover:bg-rose-50 p-1.5 rounded" data-testid="bp-delete-btn">
                      <Trash2 size={11}/>
                    </button>
                  </div>
                </div>

                {(selected.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {selected.tags.map((t,i)=><span key={i} className="text-[10px] bg-amber-50 text-amber-800 px-2 py-0.5 rounded">#{t}</span>)}
                  </div>
                )}
                {(selected.aplicabilidad || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-[10px] text-gray-500 font-semibold uppercase">Aplicable a:</span>
                    {selected.aplicabilidad.map((a,i)=><span key={i} className="text-[10px] bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded font-semibold">{a}</span>)}
                  </div>
                )}

                <pre className="text-xs text-gray-800 whitespace-pre-wrap font-sans leading-relaxed max-h-[50vh] overflow-y-auto p-3 bg-gray-50 rounded-lg" data-testid="bp-content">
{selected.contenido}
                </pre>

                <BpEvidencePanel bp={selected} onChanged={load} />
              </div>
            )
          }
        </div>
      </div>

      {showAI && <BpAIForm onClose={()=>{setShowAI(false);load();}}/>}
    </div>
  );
}

function BpEvidencePanel({ bp, onChanged }) {
  // N11 (roadmap-valoriza-jev-2026-10-11, Ola 3) · panel de evidencias
  // documentales: documento/archivo original, versión/hash, procedencia,
  // fecha, fragmento, requisito respaldado, vigencia, ámbito, responsable
  // y estado de revisión. Sin esto, score/uplift son afirmaciones sin
  // soporte (ver banner superior).
  const [evidencias, setEvidencias] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ file: null, procedencia: "", fecha_documento: "", fragmento: "", requisito_o_afirmacion: "", ambito_aplicacion: "", responsable: "" });
  const [busy, setBusy] = useState(false);

  const loadEv = async () => {
    try { const { data } = await axios.get(`${API}/tender360/best-practices/${bp.id}/evidencias`); setEvidencias(data); }
    catch { /* silencioso: la lista de evidencias no es crítica para ver el contenido */ }
  };
  useEffect(() => { loadEv(); /* eslint-disable-next-line */ }, [bp.id]);

  const upload = async () => {
    if (!form.file || !form.procedencia || !form.fragmento || !form.requisito_o_afirmacion) {
      toast.error("Archivo, procedencia, fragmento y requisito/afirmación son obligatorios"); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("meta", JSON.stringify({
        procedencia: form.procedencia, fecha_documento: form.fecha_documento || null,
        fragmento: form.fragmento, requisito_o_afirmacion: form.requisito_o_afirmacion,
        ambito_aplicacion: form.ambito_aplicacion, responsable: form.responsable,
      }));
      await axios.post(`${API}/tender360/best-practices/${bp.id}/evidencias`, fd);
      toast.success("Evidencia adjuntada (pendiente de verificación)");
      setShowForm(false);
      setForm({ file: null, procedencia: "", fecha_documento: "", fragmento: "", requisito_o_afirmacion: "", ambito_aplicacion: "", responsable: "" });
      loadEv(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir la evidencia"); }
    finally { setBusy(false); }
  };

  const verificar = async (eid) => {
    try {
      await axios.put(`${API}/tender360/best-practices/${bp.id}/evidencias/${eid}/revision`, null, { params: { estado_revision: "verificado" } });
      toast.success("Evidencia verificada"); loadEv(); onChanged();
    } catch { toast.error("Error al verificar"); }
  };

  return (
    <div className="border-t border-gray-100 pt-3" data-testid="bp-evidence-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-gray-700">Evidencias documentales ({evidencias.length})</span>
        <button onClick={() => setShowForm(v => !v)} data-testid="bp-evidence-add-btn" className="text-[11px] text-[#007934] font-semibold hover:underline">+ Adjuntar evidencia</button>
      </div>
      {evidencias.length === 0 && !showForm && (
        <p className="text-[11px] text-gray-400">Sin evidencia documental adjunta. Las cifras de score/mejora técnica son inferidas, no acreditadas.</p>
      )}
      <div className="space-y-2">
        {evidencias.map(ev => (
          <div key={ev.id} className="bg-gray-50 rounded-lg p-2 text-[11px]" data-testid={`bp-evidence-item-${ev.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-700">{ev.documento_nombre}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${ev.estado_revision === "verificado" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                {ev.estado_revision === "verificado" ? `Verificado por ${ev.aprobado_por}` : "Pendiente de verificación"}
              </span>
            </div>
            <p className="text-gray-500 mt-1">Procedencia: {ev.procedencia} · Fragmento: {ev.fragmento}</p>
            <p className="text-gray-500">Respalda: {ev.requisito_o_afirmacion}</p>
            {/* P10 (roadmap-valoriza-revision-despliegue-2026-10-12, sección 9):
                revisión periódica — nunca cambia el estado humano ya registrado,
                sólo avisa de que conviene re-confirmarlo. */}
            {ev.revision_vencida && (
              <p className="text-red-600 font-semibold mt-1" data-testid={`bp-evidence-revision-vencida-${ev.id}`}>
                ⚠ Revisión periódica vencida ({ev.dias_desde_revision} días desde la fecha del documento) — confirma que sigue siendo válida.
              </p>
            )}
            {!ev.revision_vencida && ev.revision_proxima && (
              <p className="text-amber-600 mt-1" data-testid={`bp-evidence-revision-proxima-${ev.id}`}>Revisión periódica próxima a vencer.</p>
            )}
            {ev.estado_revision !== "verificado" && (
              <button onClick={() => verificar(ev.id)} data-testid={`bp-evidence-verify-${ev.id}`} className="text-[10px] text-[#007934] font-semibold hover:underline mt-1">Marcar como verificada</button>
            )}
          </div>
        ))}
      </div>
      {showForm && (
        <div className="mt-2 bg-white border border-gray-200 rounded-lg p-3 space-y-2">
          <input type="file" data-testid="bp-evidence-file-input" onChange={e => setForm(f => ({ ...f, file: e.target.files[0] }))} className="text-[11px] w-full" />
          <input placeholder="Procedencia (obligatorio)" data-testid="bp-evidence-procedencia-input" value={form.procedencia} onChange={e => setForm(f => ({ ...f, procedencia: e.target.value }))} className="w-full border border-gray-200 rounded px-2 py-1 text-[11px]" />
          <input placeholder="Fragmento: página/cláusula/ubicación (obligatorio)" data-testid="bp-evidence-fragmento-input" value={form.fragmento} onChange={e => setForm(f => ({ ...f, fragmento: e.target.value }))} className="w-full border border-gray-200 rounded px-2 py-1 text-[11px]" />
          <input placeholder="Requisito o afirmación que respalda (obligatorio)" data-testid="bp-evidence-requisito-input" value={form.requisito_o_afirmacion} onChange={e => setForm(f => ({ ...f, requisito_o_afirmacion: e.target.value }))} className="w-full border border-gray-200 rounded px-2 py-1 text-[11px]" />
          <div className="flex gap-2">
            <input type="date" data-testid="bp-evidence-fecha-input" value={form.fecha_documento} onChange={e => setForm(f => ({ ...f, fecha_documento: e.target.value }))} className="flex-1 border border-gray-200 rounded px-2 py-1 text-[11px]" />
            <input placeholder="Ámbito de aplicación" data-testid="bp-evidence-ambito-input" value={form.ambito_aplicacion} onChange={e => setForm(f => ({ ...f, ambito_aplicacion: e.target.value }))} className="flex-1 border border-gray-200 rounded px-2 py-1 text-[11px]" />
          </div>
          <input placeholder="Responsable" data-testid="bp-evidence-responsable-input" value={form.responsable} onChange={e => setForm(f => ({ ...f, responsable: e.target.value }))} className="w-full border border-gray-200 rounded px-2 py-1 text-[11px]" />
          <button onClick={upload} disabled={busy} data-testid="bp-evidence-upload-btn" className="w-full bg-[#007934] text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50">Subir evidencia</button>
        </div>
      )}
    </div>
  );
}

function BpAIForm({ onClose }) {
  const [form, setForm] = useState({ seccion: "tecnologia", titulo: "", briefing: "", tags: "", aplicabilidad: "" });
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (!form.titulo || !form.briefing) { toast.error("Rellena título y briefing"); return; }
    setLoading(true);
    try {
      await postAiJob(`${API}/tender360/best-practices/ai-generate`, {
        seccion: form.seccion,
        titulo: form.titulo,
        briefing: form.briefing,
        tags: form.tags ? form.tags.split(",").map(s=>s.trim()).filter(Boolean) : [],
        aplicabilidad: form.aplicabilidad ? form.aplicabilidad.split(",").map(s=>s.trim()).filter(Boolean) : [],
      });
      toast.success("Best practice generado con Pando"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Error"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-xl w-full p-5" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold flex items-center gap-2"><Wand2 size={14} className="text-[#007934]"/> Generar Best Practice con Pando</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={16}/></button>
        </div>
        <div className="space-y-3">
          <Field label="Sección">
            <select value={form.seccion} onChange={e=>setForm({...form,seccion:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1 text-xs bg-white">
              <option>tecnologia</option><option>sostenibilidad</option><option>rrhh</option><option>calidad</option><option>innovacion</option><option>generico</option>
            </select>
          </Field>
          <Field label="Título *"><input value={form.titulo} onChange={e=>setForm({...form,titulo:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1 text-xs"/></Field>
          <Field label="Briefing — qué debe cubrir *"><textarea value={form.briefing} onChange={e=>setForm({...form,briefing:e.target.value})} rows={4} className="w-full border border-gray-200 rounded px-2 py-1 text-xs"/></Field>
          <Field label="Tags (coma-separado)"><input value={form.tags} onChange={e=>setForm({...form,tags:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1 text-xs"/></Field>
          <Field label="Aplicabilidad (coma-separado)"><input value={form.aplicabilidad} onChange={e=>setForm({...form,aplicabilidad:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1 text-xs"/></Field>
          <button onClick={run} disabled={loading}
                  className="w-full bg-[#007934] text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-[#006528] disabled:opacity-40 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="animate-spin" size={14}/> : <Sparkles size={14}/>} Generar
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// M6: COMPETITIVE INTELLIGENCE
// ═══════════════════════════════════════════════════════════════════════════
